/* eslint-disable max-lines */
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, from, of, throwError, timer } from 'rxjs';
import {
  catchError,
  concatMap,
  mergeMap,
  switchMap,
  tap
} from 'rxjs/operators';
import {
  AuthAccessTokenDetails,
  ILoginUserNamePasswordInput,
  LoginAuthWithCredentialsResponse,
  LoginOAuthParams,
  LoginOAuthResponse,
  SwapAuthForAccessTokens
} from '../../../models/login';
import { LoginSubmitIdentifierService } from '../../../services/login/data-services/login-submit-identifier/login-submit-identifier.service';
import { PingOneSignalsWebSdkService } from '../../../services/login/data-services/pingone-signals-web-sdk/pingone-signals-web-sdk.service';
import { ChallengeScreenError } from '../../../services/security-verification/challenge-success.service';
import { PostWithCredentialsFacadeHelperService } from '../post-with-credentials-facade-helper.service';
import { InitiateRbaLoginFacadeService } from './initiate-rba-login-facade.service';

type ErrorResponse =
  | LoginOAuthResponse
  | LoginAuthWithCredentialsResponse
  | SwapAuthForAccessTokens
  | HttpErrorResponse
  | ChallengeScreenError
  | string;

@Injectable({ providedIn: 'root' })
export class PostWithCredAndDeviceFacadeService extends PostWithCredentialsFacadeHelperService {
  private retryCount = 0;
  private identifierSubmitted = false;
  private loginSubmitIdentifierService = inject(LoginSubmitIdentifierService);
  private pingOneSignalsWebSdkService = inject(PingOneSignalsWebSdkService);

  constructor() {
    super();
  }

  postWithCredAndDeviceFlow(
    loginOAuthParams: LoginOAuthParams,
    requestInput: ILoginUserNamePasswordInput
  ): Observable<AuthAccessTokenDetails | string | boolean> {
    this.setPkceCode(loginOAuthParams);

    if (!this.flowId) {
      return this.noFlowIdError(loginOAuthParams);
    }

    return this.identifierSubmitted
      ? this.postWithCredOnly(loginOAuthParams, requestInput)
      : this.postWithCredAndDevice(loginOAuthParams, requestInput);
  }

  private setPkceCode(loginOAuthParams: LoginOAuthParams): void {
    if (this.pkceCode.codeChallenge && this.pkceCode.codeVerifier) {
      loginOAuthParams.pkceCode = this.pkceCode;
    }
  }

  private noFlowIdError(
    loginOAuthParams: LoginOAuthParams
  ): Observable<string> {
    return of(
      this.LoginRememberMeErrorUtil.checkRememberMeErrors(
        loginOAuthParams.oauthConfig,
        'ERROR_MSG_NO_FLOW_ID_GENERATED'
      )
    );
  }

  private postWithCredAndDevice(
    loginOAuthParams: LoginOAuthParams,
    requestInput: ILoginUserNamePasswordInput
  ): Observable<AuthAccessTokenDetails | string | boolean> {
    return from(this.pingOneSignalsWebSdkService.getData()).pipe(
      switchMap((deviceData: string) =>
        this.loginSubmitIdentifierService
          .loginSubmitIdentifier(
            loginOAuthParams.oauthConfig,
            this.flowId,
            requestInput.userName,
            deviceData
          )
          .pipe(
            concatMap((res: LoginAuthWithCredentialsResponse) => {
              this.identifierSubmitted = true;
              if (
                res.status ===
                loginOAuthParams.oauthConfig.USERNAME_PASSWORD_REQUIRED
              ) {
                return this.loginPostWithCred.loginPostWithCredentials(
                  loginOAuthParams.oauthConfig,
                  this.flowId,
                  requestInput,
                  true
                );
              } else {
                return throwError(res);
              }
            }),
            concatMap((res: LoginAuthWithCredentialsResponse) =>
              this.processSwapResponse(res, loginOAuthParams)
            ),
            concatMap((res: SwapAuthForAccessTokens) =>
              this.processTokenResponse(res, loginOAuthParams)
            ),
            tap(() => {
              this.identifierSubmitted = false;
            })
          )
      ),
      catchError((error) =>
        this.handleErrorWithRetry(error, loginOAuthParams, requestInput)
      )
    );
  }

  private postWithCredOnly(
    loginOAuthParams: LoginOAuthParams,
    requestInput: ILoginUserNamePasswordInput
  ): Observable<AuthAccessTokenDetails | string | boolean> {
    return this.loginPostWithCred
      .loginPostWithCredentials(
        loginOAuthParams.oauthConfig,
        this.flowId,
        requestInput,
        true
      )
      .pipe(
        concatMap((res: LoginAuthWithCredentialsResponse) =>
          this.processSwapResponse(res, loginOAuthParams)
        ),
        concatMap((res: SwapAuthForAccessTokens) =>
          this.processTokenResponse(res, loginOAuthParams)
        ),
        tap(() => {
          this.identifierSubmitted = false;
        }),
        catchError((error) =>
          this.handleErrorWithRetry(error, loginOAuthParams, requestInput)
        )
      );
  }

  private handleErrorWithRetry(
    error: ErrorResponse,
    loginOAuthParams: LoginOAuthParams,
    requestInput: ILoginUserNamePasswordInput
  ): Observable<AuthAccessTokenDetails | string | boolean> {
    if (typeof error === 'string') return of(error);
    const retryTimer = loginOAuthParams.oauthConfig.LOGIN_RETRY_TIMER || 1000;
    const maxRetryCount = loginOAuthParams.oauthConfig.LOGIN_RETRY_COUNT || 0;
    return timer(retryTimer).pipe(
      mergeMap(() => {
        this.retryCount++;
        if (this.retryCount !== maxRetryCount) {
          loginOAuthParams.pkceCode =
            this.loginRememberMeUtils.generateCodeChallenge();
          return this.handlePostWithCredAndDeviceError(
            error,
            loginOAuthParams,
            requestInput
          );
        }
        this.retryCount = 0;
        return of(
          this.LoginRememberMeErrorUtil.checkRememberMeErrors(
            loginOAuthParams.oauthConfig,
            error
          )
        );
      })
    );
  }

  private handlePostWithCredAndDeviceError(
    error: ErrorResponse,
    loginOAuthParams: LoginOAuthParams,
    requestInput: ILoginUserNamePasswordInput
  ): Observable<AuthAccessTokenDetails | string | boolean> {
    const initiateRbaLoginFacade = this.injector.get(
      InitiateRbaLoginFacadeService
    );
    if (
      !loginOAuthParams.pkceCode.codeChallenge &&
      !loginOAuthParams.pkceCode.codeVerifier
    ) {
      loginOAuthParams.pkceCode =
        this.loginRememberMeUtils.generateCodeChallenge();
    }
    if (this.shouldRetryLogin(error)) {
      return initiateRbaLoginFacade.initiateLogin(
        loginOAuthParams,
        requestInput
      );
    }
    return of(
      this.LoginRememberMeErrorUtil.checkRememberMeErrors(
        loginOAuthParams.oauthConfig,
        error
      )
    );
  }
}
